"use strict";
// Queue Engine schemas (Spec 1 of 5).
//
// Shapes the Engine's contract surface: REST request/response bodies,
// WebSocket event payloads, and the domain entities returned over the
// wire. Mirror this module's structure in apps/server/src/queue-engine/.
Object.defineProperty(exports, "__esModule", { value: true });
exports.UpdateOrderStateInput = exports.Order = exports.OrderItemLineSnapshot = exports.OrderState = exports.OrderStates = exports.PlaceOrderInput = exports.OrderItemLine = exports.UpdateMenuItemInput = exports.CreateMenuItemInput = exports.MenuItem = exports.AttendantEvent = exports.TicketHolderEvent = exports.QueueDisplayEvent = exports.QueueEngineSubscribeInput = exports.PushSubscriptionInput = exports.PublicJoinResponse = exports.PublicTicketStatus = exports.PublicJoinInput = exports.NextTicketInput = exports.UpdateSessionInput = exports.OpenSessionInput = exports.ReplaceBindingsInput = exports.UpdateCounterInput = exports.CreateCounterInput = exports.UpdateQueueInput = exports.CreateQueueInput = exports.QueueScheduleInput = exports.PushSubscription = exports.Ticket = exports.AttendantSession = exports.CounterQueueBinding = exports.PhysicalCounter = exports.Queue = exports.TicketIssuedVia = exports.TicketIssuedViaValues = exports.AttendantSessionState = exports.AttendantSessionStates = exports.TicketState = exports.TicketStates = exports.QueueState = exports.QueueStates = void 0;
const zod_1 = require("zod");
const resource_schema_js_1 = require("./resource.schema.js");
const Uuid = zod_1.z.string().uuid();
const Iso = zod_1.z.string().datetime();
const HHMMSS = zod_1.z.string().regex(/^\d{2}:\d{2}:\d{2}$/, 'expected HH:MM:SS');
// -------------------- Enums --------------------
exports.QueueStates = ['open', 'paused', 'closed'];
exports.QueueState = zod_1.z.enum(exports.QueueStates);
exports.TicketStates = ['waiting', 'called', 'serving', 'done', 'no_show', 'cancelled'];
exports.TicketState = zod_1.z.enum(exports.TicketStates);
exports.AttendantSessionStates = ['active', 'paused', 'ended'];
exports.AttendantSessionState = zod_1.z.enum(exports.AttendantSessionStates);
exports.TicketIssuedViaValues = ['kiosk', 'pwa', 'attendant'];
exports.TicketIssuedVia = zod_1.z.enum(exports.TicketIssuedViaValues);
// -------------------- Entities --------------------
exports.Queue = zod_1.z.object({
    id: Uuid,
    tenant_id: Uuid,
    name_i18n: resource_schema_js_1.NameI18n,
    letter_prefix: zod_1.z.string().length(1).regex(/^[A-Z]$/),
    state: exports.QueueState,
    schedule_open: HHMMSS.nullable().optional(),
    schedule_close: HHMMSS.nullable().optional(),
    schedule_days: zod_1.z.array(zod_1.z.number().int().min(1).max(7)).nullable().optional(),
    next_number: zod_1.z.number().int().nonnegative(),
    last_reset_at: Iso.nullable().optional(),
    created_at: Iso,
    updated_at: Iso,
});
exports.PhysicalCounter = zod_1.z.object({
    id: Uuid,
    tenant_id: Uuid,
    group_id: Uuid.nullable().optional(),
    label_i18n: resource_schema_js_1.NameI18n,
    station_code: zod_1.z.string().min(1).max(16),
    created_at: Iso,
    updated_at: Iso,
});
exports.CounterQueueBinding = zod_1.z.object({
    counter_id: Uuid,
    queue_id: Uuid,
    priority_order: zod_1.z.number().int().nonnegative(),
});
exports.AttendantSession = zod_1.z.object({
    id: Uuid,
    tenant_id: Uuid,
    attendant_user_id: Uuid,
    counter_id: Uuid,
    serving_queues: zod_1.z.array(Uuid),
    current_ticket_id: Uuid.nullable().optional(),
    state: exports.AttendantSessionState,
    started_at: Iso,
    ended_at: Iso.nullable().optional(),
});
exports.Ticket = zod_1.z.object({
    id: Uuid,
    tenant_id: Uuid,
    queue_id: Uuid,
    number: zod_1.z.number().int().positive(),
    label: zod_1.z.string().regex(/^[A-Z]\d{3,}$/),
    state: exports.TicketState,
    claim_token: zod_1.z.string().length(64),
    counter_id: Uuid.nullable().optional(),
    attendant_session_id: Uuid.nullable().optional(),
    issued_via: exports.TicketIssuedVia,
    recall_count: zod_1.z.number().int().nonnegative(),
    issued_at: Iso,
    called_at: Iso.nullable().optional(),
    serving_started_at: Iso.nullable().optional(),
    completed_at: Iso.nullable().optional(),
});
exports.PushSubscription = zod_1.z.object({
    id: Uuid,
    ticket_id: Uuid,
    endpoint: zod_1.z.string().url(),
    keys_p256dh: zod_1.z.string().min(1),
    keys_auth: zod_1.z.string().min(1),
    user_agent: zod_1.z.string().nullable().optional(),
    created_at: Iso,
    expires_at: Iso.nullable().optional(),
});
// -------------------- REST Inputs --------------------
exports.QueueScheduleInput = zod_1.z.object({
    schedule_open: HHMMSS,
    schedule_close: HHMMSS,
    schedule_days: zod_1.z.array(zod_1.z.number().int().min(1).max(7)).optional(),
});
exports.CreateQueueInput = zod_1.z.object({
    name_i18n: resource_schema_js_1.NameI18n,
    letter_prefix: zod_1.z.string().length(1).regex(/^[A-Z]$/),
    schedule: exports.QueueScheduleInput.optional(),
});
exports.UpdateQueueInput = zod_1.z.object({
    name_i18n: resource_schema_js_1.NameI18n.optional(),
    schedule: exports.QueueScheduleInput.nullable().optional(),
}).refine((v) => Object.keys(v).length > 0, { message: 'at_least_one_field_required' });
exports.CreateCounterInput = zod_1.z.object({
    label_i18n: resource_schema_js_1.NameI18n,
    station_code: zod_1.z.string().min(1).max(16),
    group_id: Uuid.optional(),
});
exports.UpdateCounterInput = zod_1.z.object({
    label_i18n: resource_schema_js_1.NameI18n.optional(),
    station_code: zod_1.z.string().min(1).max(16).optional(),
    group_id: Uuid.nullable().optional(),
}).refine((v) => Object.keys(v).length > 0, { message: 'at_least_one_field_required' });
exports.ReplaceBindingsInput = zod_1.z.object({
    bindings: zod_1.z.array(zod_1.z.object({
        queue_id: Uuid,
        priority_order: zod_1.z.number().int().nonnegative(),
    })),
});
exports.OpenSessionInput = zod_1.z.object({
    counter_id: Uuid,
    serving_queues: zod_1.z.array(Uuid).min(1),
});
exports.UpdateSessionInput = zod_1.z.object({
    serving_queues: zod_1.z.array(Uuid).min(1),
});
exports.NextTicketInput = zod_1.z.object({
    queue_id: Uuid.optional(),
});
exports.PublicJoinInput = zod_1.z.object({
    queue_id: Uuid,
    source: zod_1.z.enum(['pwa', 'kiosk']),
});
exports.PublicTicketStatus = zod_1.z.object({
    state: exports.TicketState,
    position: zod_1.z.number().int().nonnegative(),
    eta_minutes: zod_1.z.number().int().nonnegative(),
    counter_label: zod_1.z.string().nullable().optional(),
    recall_count: zod_1.z.number().int().nonnegative(),
});
exports.PublicJoinResponse = zod_1.z.object({
    ticket_id: Uuid,
    claim_token: zod_1.z.string().length(64),
    label: zod_1.z.string(),
    position: zod_1.z.number().int().nonnegative(),
    eta_minutes: zod_1.z.number().int().nonnegative(),
});
exports.PushSubscriptionInput = zod_1.z.object({
    endpoint: zod_1.z.string().url(),
    keys: zod_1.z.object({
        p256dh: zod_1.z.string().min(1),
        auth: zod_1.z.string().min(1),
    }),
    user_agent: zod_1.z.string().optional(),
});
// -------------------- WS Events --------------------
/**
 * Client → server `subscribe` / `unsubscribe` payload on /ws/queue-engine.
 * The client names a room KIND (+ target id); the gateway derives the
 * tenant from the socket's verified principal — never from this payload.
 */
exports.QueueEngineSubscribeInput = zod_1.z.discriminatedUnion('room', [
    zod_1.z.object({ room: zod_1.z.literal('queue'), queue_id: Uuid }),
    zod_1.z.object({ room: zod_1.z.literal('counter'), counter_id: Uuid }),
    zod_1.z.object({ room: zod_1.z.literal('kitchen') }),
]);
exports.QueueDisplayEvent = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({
        type: zod_1.z.literal('ticket.called'),
        ticket_id: Uuid, label: zod_1.z.string(),
        counter_id: Uuid, counter_label: zod_1.z.string(),
        recall_count: zod_1.z.number().int().nonnegative(),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('ticket.serving'),
        ticket_id: Uuid, label: zod_1.z.string(),
        counter_id: Uuid, counter_label: zod_1.z.string(),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('ticket.done'),
        ticket_id: Uuid, label: zod_1.z.string(),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('queue.state'),
        queue_id: Uuid, state: exports.QueueState,
    }),
    zod_1.z.object({
        type: zod_1.z.literal('queue.stats'),
        queue_id: Uuid,
        waiting_count: zod_1.z.number().int().nonnegative(),
        average_wait_minutes: zod_1.z.number().int().nonnegative(),
    }),
]);
exports.TicketHolderEvent = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({
        type: zod_1.z.literal('position'),
        position: zod_1.z.number().int().nonnegative(),
        eta_minutes: zod_1.z.number().int().nonnegative(),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('called'),
        counter_label: zod_1.z.string(),
        recall_count: zod_1.z.number().int().nonnegative(),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('cancelled'),
        reason: zod_1.z.enum(['queue_closed', 'admin', 'self']),
    }),
    zod_1.z.object({ type: zod_1.z.literal('done') }),
]);
exports.AttendantEvent = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({
        type: zod_1.z.literal('queue.stats'),
        queue_id: Uuid,
        waiting_count: zod_1.z.number().int().nonnegative(),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('session.changed'),
        current_ticket_id: Uuid.nullable(),
    }),
]);
// =========================================================================
// Restaurant extension (Spec 5 of 5)
// =========================================================================
// MVP shapes for menu items + orders. Entities mirror the Engine's wire
// shape (snake_case, jsonb name_i18n). Order line snapshots freeze the
// price/name at order time so subsequent menu edits don't rewrite history.
exports.MenuItem = zod_1.z.object({
    id: Uuid,
    tenant_id: Uuid,
    queue_id: Uuid.nullable(),
    name_i18n: resource_schema_js_1.NameI18n,
    price_cents: zod_1.z.number().int().nonnegative(),
    available: zod_1.z.boolean(),
    created_at: Iso,
    updated_at: Iso,
});
exports.CreateMenuItemInput = zod_1.z.object({
    name_i18n: resource_schema_js_1.NameI18n,
    queue_id: Uuid.optional(),
    price_cents: zod_1.z.number().int().nonnegative(),
    available: zod_1.z.boolean().default(true),
});
exports.UpdateMenuItemInput = zod_1.z.object({
    name_i18n: resource_schema_js_1.NameI18n.optional(),
    queue_id: Uuid.nullable().optional(),
    price_cents: zod_1.z.number().int().nonnegative().optional(),
    available: zod_1.z.boolean().optional(),
}).refine((v) => Object.keys(v).length > 0, { message: 'at_least_one_field_required' });
exports.OrderItemLine = zod_1.z.object({
    menu_item_id: Uuid,
    qty: zod_1.z.number().int().positive(),
});
exports.PlaceOrderInput = zod_1.z.object({
    // PWA links an order to an existing ticket via claim_token in the
    // request guard; admin/kiosk surface can place a free-standing order.
    ticket_id: Uuid.optional(),
    items: zod_1.z.array(exports.OrderItemLine).min(1),
    customer_note: zod_1.z.string().max(500).optional(),
});
exports.OrderStates = ['pending', 'preparing', 'ready', 'served', 'cancelled'];
exports.OrderState = zod_1.z.enum(exports.OrderStates);
exports.OrderItemLineSnapshot = zod_1.z.object({
    menu_item_id: Uuid,
    qty: zod_1.z.number().int().positive(),
    name_snapshot: resource_schema_js_1.NameI18n,
    price_cents_snapshot: zod_1.z.number().int().nonnegative(),
});
exports.Order = zod_1.z.object({
    id: Uuid,
    tenant_id: Uuid,
    ticket_id: Uuid.nullable(),
    state: exports.OrderState,
    items: zod_1.z.array(exports.OrderItemLineSnapshot),
    total_cents: zod_1.z.number().int().nonnegative(),
    customer_note: zod_1.z.string().nullable(),
    created_at: Iso,
    updated_at: Iso,
});
exports.UpdateOrderStateInput = zod_1.z.object({
    state: exports.OrderState,
});
//# sourceMappingURL=queue-engine.schema.js.map